/**
 * Paged PostgREST reads.
 *
 * PostgREST caps an unranged select at `max_rows` (1,000 here) and returns the
 * truncation silently — no error, no flag. Any query that feeds a total, a
 * picker, or a chart therefore has to page, or it will confidently report a
 * partial answer.
 *
 * This became load-bearing when the shared date filter started offering
 * all-time, 365-day and fiscal-year windows on screens whose queries had only
 * ever been asked for a month. Several of those screens were already truncating
 * at their old maximum range; the broad presets just made it easy to notice.
 */

/** PostgREST's configured `max_rows`. A short page means the last page. */
export const POSTGREST_PAGE_SIZE = 1000;

export interface PagedSelectResult<T> {
  data: T[] | null;
  error: unknown;
}

/**
 * Reads every row a query matches, one page at a time.
 *
 * `selectPage` receives an inclusive `[from, to]` row range and should apply it
 * to the query (`builder.range(from, to)`). Throws on the first page error, so
 * a partial result is never mistaken for a complete one.
 *
 * **The query MUST end in a deterministic total order** — finish with a unique
 * column such as `id`. Offset paging asks the database for rows N..M of a
 * result set; without a total order Postgres may order two pages differently
 * and the same row can be skipped or returned twice. That is worse than the
 * truncation this helper exists to fix, because the row count still looks
 * plausible.
 */
export async function fetchAllRows<T>(
  selectPage: (from: number, to: number) => PromiseLike<PagedSelectResult<T>>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += POSTGREST_PAGE_SIZE) {
    const { data, error } = await selectPage(from, from + POSTGREST_PAGE_SIZE - 1);
    if (error) throw error;
    const page = data ?? [];
    rows.push(...page);
    if (page.length < POSTGREST_PAGE_SIZE) break;
  }
  return rows;
}

export class PagedReadKeptChangingError extends Error {
  constructor(maxAttempts: number) {
    super(`Rows kept changing while they were being read (${maxAttempts} attempts); try again.`);
    this.name = "PagedReadKeptChangingError";
  }
}

export interface RowCountResult {
  count: number | null;
  error: unknown;
}

/**
 * Keyset-paged read for totals that must add up (ledger reports).
 *
 * Every request is its own database snapshot. With offset paging, a row that
 * becomes visible between two pages — a journal posted while a report is
 * loading — shifts already-read rows onto the next page, so one row is read
 * twice and the new one is missed. The row count still looks right, so a count
 * check cannot catch it, and neither can a created-at cutoff, because a draft
 * entry keeps its old timestamps when it is posted.
 *
 * This pages by keyset instead: each request asks for the rows whose unique `id`
 * is greater than the last one read. A row can then never be read twice and a
 * page never depends on how many rows came before it. A row that appears behind
 * the cursor is simply not in the result, and that is what the count check
 * catches: after paging, the matching rows are counted, and the read is kept only
 * if the count equals the number of rows read. Because duplicates are impossible,
 * equal counts mean the same set. Otherwise the whole read is retried, and one
 * that never settles throws, so a skewed ledger is never returned as complete.
 *
 * The one case equal counts cannot see is a row appearing behind the cursor while
 * another row leaves the filter ahead of it within the same few seconds (an entry
 * posted while a different one is un-posted). Fully closing that needs a
 * server-side snapshot, which is the durable `finance_trial_balance` function
 * tracked on #6513.
 *
 * A single short page is one request and therefore one snapshot; it is returned
 * without counting, so small organizations pay nothing extra.
 *
 * `selectPage` receives the last `id` read (null for the first page) and must
 * apply `.gt("id", afterId)` when it is set, `.order("id", { ascending: true })`
 * and `.limit(POSTGREST_PAGE_SIZE)`. `countRows` must apply exactly the filters
 * `selectPage` does, except for the keyset condition.
 */
export async function fetchAllRowsKeyset<T extends { id: string }>(
  selectPage: (afterId: string | null) => PromiseLike<PagedSelectResult<T>>,
  countRows: () => PromiseLike<RowCountResult>,
  maxAttempts = 3,
): Promise<T[]> {
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const rows: T[] = [];
    let afterId: string | null = null;
    for (;;) {
      const { data, error } = await selectPage(afterId);
      if (error) throw error;
      const page = data ?? [];
      rows.push(...page);
      if (page.length < POSTGREST_PAGE_SIZE) break;
      afterId = page[page.length - 1].id;
    }
    if (rows.length < POSTGREST_PAGE_SIZE) return rows;
    const after = await countRows();
    if (after.error) throw after.error;
    if (after.count === rows.length) return rows;
  }
  throw new PagedReadKeptChangingError(maxAttempts);
}
